import { useState, useEffect, useMemo } from "react";
import { useAdminTransactions } from "../../../api/admin";

export default function AdminTransactions() {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [showDetails, setShowDetails] = useState<number | null>(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filterStatus, filterType]);

  const {
    data: transactionsResponse,
    isLoading,
    refetch,
  } = useAdminTransactions({
    search: debouncedSearch || undefined,
    page: currentPage,
  });

  const transactionsData = transactionsResponse?.results?.data || [];
  const stats = transactionsResponse?.results || {};
  const totalCount = transactionsResponse?.count || 0;
  const hasNext = !!transactionsResponse?.next;
  const hasPrevious = !!transactionsResponse?.previous;

  // Client-side filtering for status and type
  const filteredTransactions = useMemo(() => {
    return transactionsData.filter((transaction: any) => {
      const matchesStatus =
        filterStatus === "all" ||
        transaction.status.toLowerCase() === filterStatus.toLowerCase();

      const matchesType =
        filterType === "all" ||
        transaction.type.toLowerCase() === filterType.toLowerCase();

      return matchesStatus && matchesType;
    });
  }, [transactionsData, filterStatus, filterType]);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
      case "success":
        return "bg-purple-100 text-purple-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "failed":
        return "bg-red-100 text-red-800";
      case "cancelled":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case "airtime":
        return "bg-blue-100 text-blue-800";
      case "data":
        return "bg-purple-100 text-purple-800";
      case "electricity":
        return "bg-yellow-100 text-yellow-800";
      case "cable":
      case "cable-tv":
        return "bg-red-100 text-red-800";
      case "exam":
      case "exam-pin":
        return "bg-pink-100 text-pink-800";
      case "funding":
      case "wallet-funding":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case "airtime":
        return "ri-phone-fill";
      case "data":
        return "ri-wifi-fill";
      case "electricity":
        return "ri-flashlight-fill";
      case "cable":
      case "cable-tv":
        return "ri-tv-fill";
      case "exam":
      case "exam-pin":
        return "ri-file-text-fill";
      case "funding":
      case "wallet-funding":
        return "ri-wallet-fill";
      default:
        return "ri-file-list-fill";
    }
  };

  const exportTransactions = () => {
    const csvContent = [
      [
        "ID",
        "User",
        "Type",
        "Amount",
        "Direction",
        "Status",
        "Reference",
        "Created At",
      ].join(","),
      ...filteredTransactions.map((t: any) =>
        [
          t.id,
          t.profile?.full_name || t.profile?.username || "",
          t.type,
          t.amount,
          t.direction,
          t.status,
          t.tx_ref,
          new Date(t.created_at).toLocaleString(),
        ].join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `transactions_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const totalAmount = stats.total_transaction_amount || 0;
  const totalTransactions = stats.total_transaction_count || 0;
  const todayCount = stats.today_transaction_count || 0;
  const todayAmount = stats.today_transaction_amount || 0;

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            Transactions Management
          </h1>
          <p className="text-gray-600 mt-1">
            Monitor and manage all platform transactions
          </p>
        </div>
        <div className="flex items-center space-x-3 mt-4 md:mt-0">
          <button
            onClick={exportTransactions}
            disabled={filteredTransactions.length === 0}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap disabled:opacity-50"
          >
            <i className="ri-download-fill mr-2"></i>
            Export CSV
          </button>
          <button
            onClick={() => refetch()}
            disabled={isLoading}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap disabled:opacity-50"
          >
            <i
              className={`ri-refresh-fill mr-2 ${isLoading ? "animate-spin" : ""}`}
            ></i>
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Transactions</p>
              <p className="text-xl font-bold text-gray-900">
                {totalTransactions.toLocaleString()}
              </p>
            </div>
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <i className="ri-file-list-3-fill text-blue-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Amount</p>
              <p className="text-xl font-bold text-gray-900">
                ₦{totalAmount.toLocaleString()}
              </p>
            </div>
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <i className="ri-money-dollar-circle-fill text-purple-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Today's Transactions</p>
              <p className="text-xl font-bold text-purple-600">
                {todayCount.toLocaleString()}
              </p>
            </div>
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <i className="ri-check-double-fill text-purple-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Today's Amount</p>
              <p className="text-xl font-bold text-orange-600">
                ₦{todayAmount.toLocaleString()}
              </p>
            </div>
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <i className="ri-calendar-check-fill text-orange-600"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Transactions
            </label>
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by user, reference, etc."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm pr-8"
            >
              <option value="all">All Status</option>
              <option value="success">Success</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Type
            </label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm pr-8"
            >
              <option value="all">All Types</option>
              <option value="airtime">Airtime</option>
              <option value="data">Data</option>
              <option value="electricity">Electricity</option>
              <option value="cable">Cable TV</option>
              <option value="exam">Exam Pin</option>
              <option value="funding">Wallet Funding</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm("");
                setFilterStatus("all");
                setFilterType("all");
              }}
              className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm whitespace-nowrap"
            >
              <i className="ri-refresh-fill mr-2"></i>
              Reset Filters
            </button>
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center">
            <i className="ri-loader-4-line animate-spin text-4xl text-red-600 mb-4"></i>
            <p className="text-gray-600">Loading transactions...</p>
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div className="p-12 text-center">
            <i className="ri-file-list-line text-4xl text-gray-400 mb-4"></i>
            <p className="text-gray-600">No transactions found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Transaction
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredTransactions.map((transaction: any) => (
                    <tr key={transaction.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            #{transaction.id}
                          </div>
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {transaction.tx_ref || "N/A"}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {transaction.profile?.full_name ||
                              transaction.profile?.username ||
                              "Unknown"}
                          </div>
                          <div className="text-sm text-gray-500">
                            {transaction.profile?.email || ""}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                            <i
                              className={`${getTypeIcon(transaction.type)} text-gray-600`}
                            ></i>
                          </div>
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(transaction.type)}`}
                          >
                            {transaction.type}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          ₦{parseFloat(transaction.amount).toLocaleString()}
                        </div>
                        {transaction.direction && (
                          <div
                            className={`text-sm ${transaction.direction === "credit" ? "text-purple-600" : "text-red-600"}`}
                          >
                            {transaction.direction === "credit"
                              ? "↓ Credit"
                              : "↑ Debit"}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(transaction.status)}`}
                        >
                          {transaction.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(transaction.created_at).toLocaleDateString()}
                        <div className="text-xs text-gray-400">
                          {new Date(
                            transaction.created_at,
                          ).toLocaleTimeString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() =>
                            setShowDetails(
                              showDetails === transaction.id
                                ? null
                                : transaction.id,
                            )
                          }
                          className="w-8 h-8 flex items-center justify-center text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <i className="ri-eye-fill"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing{" "}
                  <span className="font-medium">
                    {filteredTransactions.length}
                  </span>{" "}
                  of <span className="font-medium">{totalCount}</span>{" "}
                  transactions
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                    disabled={!hasPrevious || isLoading}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                    Previous
                  </button>
                  <span className="px-3 py-1 text-sm text-gray-700">
                    Page {currentPage}
                  </span>
                  <button
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                    disabled={!hasNext || isLoading}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Transaction Details Modal */}
      {showDetails &&
        (() => {
          const transaction = transactionsData.find(
            (t: any) => t.id === showDetails,
          );
          if (!transaction) return null;

          return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Transaction Details
                    </h3>
                    <button
                      onClick={() => setShowDetails(null)}
                      className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <i className="ri-close-fill text-xl"></i>
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-500">
                          Transaction ID
                        </label>
                        <p className="text-sm font-medium text-gray-900">
                          #{transaction.id}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500">
                          Reference
                        </label>
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {transaction.tx_ref || "N/A"}
                        </p>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-500">
                        User
                      </label>
                      <p className="text-sm font-medium text-gray-900">
                        {transaction.profile?.full_name ||
                          transaction.profile?.username}
                      </p>
                      <p className="text-sm text-gray-500">
                        {transaction.profile?.email}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-500">
                          Type
                        </label>
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(transaction.type)}`}
                        >
                          {transaction.type}
                        </span>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500">
                          Direction
                        </label>
                        <p
                          className={`text-sm font-medium ${transaction.direction === "credit" ? "text-purple-600" : "text-red-600"}`}
                        >
                          {transaction.direction || "N/A"}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-500">
                          Amount
                        </label>
                        <p className="text-sm font-medium text-gray-900">
                          ₦{parseFloat(transaction.amount).toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500">
                          Status
                        </label>
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(transaction.status)}`}
                        >
                          {transaction.status}
                        </span>
                      </div>
                    </div>

                    {transaction.metadata && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-2">
                          Metadata
                        </label>
                        <div className="bg-gray-50 rounded-lg p-3 text-xs font-mono">
                          <pre className="whitespace-pre-wrap">
                            {JSON.stringify(transaction.metadata, null, 2)}
                          </pre>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-500">
                          Created At
                        </label>
                        <p className="text-sm font-medium text-gray-900">
                          {new Date(transaction.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500">
                          Updated At
                        </label>
                        <p className="text-sm font-medium text-gray-900">
                          {new Date(transaction.updated_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
}
